import React from 'react'
import { useQuery } from '@tanstack/react-query'
import type { DiagramSource } from '../domains/dashboard/models'
import type { Role } from '../core/repository/DiagramRepository'
import {
  sanitizeFolderChildrenData,
  selectFolderChildrenWithPurgeTombstones,
  usePurgePendingStore,
} from '../dashboard/purgePendingStore'

export type FolderData = {
  folder: {
    id: string
    name: string
    parentId: string | null
    orgId: string
    ownerUserId: string
    createdAt: string
    updatedAt: string
    domain?: string
    metadata?: Record<string, unknown>
    source?: 'native' | 'legacy'
    archived?: boolean
  }
}

export type FolderChildren = {
  folders: Array<{
    id: string
    name: string
    parentId: string | null
    orgId: string
    ownerUserId: string
    createdAt: string
    updatedAt: string
    domain?: string
    source?: DiagramSource
  }>
  diagrams: Array<{
    id: string
    name: string
    folderId: string
    orgId: string
    ownerUserId: string | null
    type: string
    icon?: string | null
    role?: Role
    source?: DiagramSource
    isTemplate: boolean
    thumbnailUrl: string | null
    createdAt: string
    updatedAt: string
  }>
}

export function useFolderData(folderId?: string) {
  return useQuery<FolderData>({
    queryKey: ['folder', folderId, 'data'],
    enabled: Boolean(folderId),
    queryFn: async () => {
      const response = await fetch(`/api/folders/${folderId}`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Failed to fetch folder data: ${response.statusText}`);
      }
      return response.json();
    },
    staleTime: 30_000,
  })
}

export function useFolderChildren(folderId?: string) {
  const purgeRevision = usePurgePendingStore((state) => state.revision)
  return useQuery<FolderChildren>({
    queryKey: ['folder', folderId, 'children'],
    enabled: Boolean(folderId),
    queryFn: async () => {
      // Fetch both folders and diagrams in parallel
      const [foldersResponse, diagramsResponse] = await Promise.all([
        fetch(`/api/folders?parentId=${folderId}`, { credentials: 'include' }),
        fetch(`/api/folders/${folderId}/diagrams`, { credentials: 'include' })
      ]);
      
      if (!foldersResponse.ok) {
        throw new Error(`Failed to fetch folder children: ${foldersResponse.statusText}`);
      }
      if (!diagramsResponse.ok) {
        throw new Error(`Failed to fetch folder diagrams: ${diagramsResponse.statusText}`);
      }
      
      const [foldersData, diagramsData] = await Promise.all([
        foldersResponse.json(),
        diagramsResponse.json()
      ]);
      
      return sanitizeFolderChildrenData({
        folders: foldersData.folders || [],
        diagrams: diagramsData.diagrams || [],
      });
    },
    // Charts are created in a separate editor tab. Refresh the visible folder
    // listing even when this dashboard tab remains mounted (or embedded).
    refetchInterval: 15_000,
    refetchOnWindowFocus: 'always',
    staleTime: 30_000,
    select: React.useCallback(
      (data: FolderChildren) => selectFolderChildrenWithPurgeTombstones(data),
      [purgeRevision],
    ),
  })
}
